import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../../../base/customHooks'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import IPButton from '../../../../../components/IPButton'
import IPCheckbox from '../../../../../components/IPCheckBox'
import IPInput from '../../../../../components/IPInput'
import IPSelectBox, { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import Table from '../../../../../components/Table'
import { compactCardNumber, formatCardNumberGroups } from '../../../../../base/utils/cardNumber'
import { CreditCardModel } from '../models/models'
import { ChangeStatusCreditCardApi } from '../services/changeStatusCreditCardApi'
import { GetDealerCreditCardsApi } from '../services/getDealerCreditCardsApi'
import { GetCreditCardDeclarationApi } from '../services/getCreditCardDeclarationApi'
import AdminPageAuthGuard from '../../../components/AdminPageAuthGuard'

interface ICreditCardState {
  selectedStatus?: ISelectOption
  dealerCode?: string
  dealerName?: string
  cardNumber?: string
  cardName?: string
  isActive?: boolean
}

function Index(props: BaseComponentWithContextProps) {
  const getInitialState = (): ICreditCardState => {
    return {}
  }

  const intialState = getInitialState()
  const [state, setState] = useCustomReducer<ICreditCardState>(intialState)
  const [cardList, setCardList] = useState<CreditCardModel[]>([])

  const filterStatus: ISelectOption[] = [
    { label: 'Onaylandı', value: 2 },
    { label: 'Reddedildi', value: 1 },
    { label: 'Beklemede', value: 0 },
  ]

  const getList = async () => {
    props.showLoading()

    const reqModel: any = {
      DealerCode: state.dealerCode,
      DealerName: state.dealerName,
      CardName: state.cardName,
      CardNumber: state.cardNumber,
      Status: state.selectedStatus?.value,
      Active: state.isActive,
    }

    const netapi: GetDealerCreditCardsApi = new GetDealerCreditCardsApi({})
    try {
      await netapi
        .getDealerCreditCards(reqModel)
        .then(result => {
          if (result) setCardList(result)
          props.hideLoading()
        })
        .catch((err: any) => {
          props.hideLoading()
          Swal.fire({
            icon: 'error',
            title: err?.description ?? 'Bir hata oluştu',
            showConfirmButton: true,
            allowOutsideClick: false,
          })
        })
    } catch (err: any) {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: err?.description ?? 'Bir hata oluştu',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
  }

  useEffect(() => {
    getList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Beyan belgesi tam kart numarası içerdiği için context servisine yazılmıyor; kimlik
  // doğrulamalı endpoint'ten base64 gelip tarayıcıda Blob'a çevrilerek açılıyor.
  const openDeclaration = async (id: number) => {
    props.showLoading()
    try {
      const declarationApi: GetCreditCardDeclarationApi = new GetCreditCardDeclarationApi({})
      const result = await declarationApi.getDeclaration(id)

      if (result && result.ContentBase64) {
        const bytes = Uint8Array.from(atob(result.ContentBase64), c => c.charCodeAt(0))
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description ?? 'Beyan belgesi açılamadı',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
    props.hideLoading()
  }

  const changeStatus = async (id: number, status: number, active: boolean, rejectReason?: string) => {
    props.showLoading()

    const reqModel: any = {
      Id: id,
      Status: status,
      Active: active,
      RejectReason: rejectReason,
    }

    const netapi: ChangeStatusCreditCardApi = new ChangeStatusCreditCardApi({})
    try {
      await netapi
        .changeStatusCreditCard(reqModel)
        .then(result => {
          if (result) getList()
          props.hideLoading()
        })
        .catch((err: any) => {
          props.hideLoading()
          Swal.fire({
            icon: 'error',
            title: err?.description ?? 'Bir hata oluştu',
            showConfirmButton: true,
            allowOutsideClick: false,
          })
        })
    } catch (err: any) {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: err?.description ?? 'Bir hata oluştu',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
  }

  // Red nedeni zorunlu; bayi kendi Kredi Kartlarım ekranında bu metni görür.
  const reject = async (item: CreditCardModel) => {
    const result = await Swal.fire({
      title: 'Kartı Reddet',
      input: 'textarea',
      inputLabel: 'Red nedeni (bayi bu metni görecektir)',
      inputValue: item.Status === 1 ? item.RejectReason || '' : '',
      inputAttributes: { maxlength: '500' },
      showCancelButton: true,
      confirmButtonText: 'Reddet',
      cancelButtonText: 'Vazgeç',
      allowOutsideClick: false,
      inputValidator: value => (!value || !value.trim() ? 'Red nedeni giriniz.' : null),
    })

    if (result.isConfirmed) changeStatus(item.Id, 1, item.Active, result.value.trim())
  }

  const columnDefinitions = [
    { Header: '#', accessor: 'order' },
    { Header: 'Bayi Kodu', accessor: 'bayiKodu' },
    { Header: 'Bayi Adı', accessor: 'bayiAdı' },
    { Header: 'Kart Numarası', accessor: 'cardNumber' },
    { Header: 'Kart Sahibi', accessor: 'cardName' },
    { Header: 'Kart Resmi', accessor: 'image' },
    { Header: 'Onay Belgesi', accessor: 'approvalDocument' },
    { Header: 'İmza Sirküsü', accessor: 'imzaSirkusu' },
    { Header: 'Oluşturulma Tarihi', accessor: 'create' },
    { Header: 'Aktiflik', accessor: 'enabled' },
    { Header: 'Silindi', accessor: 'deleted' },
    { Header: 'Kullanılabilir', accessor: 'active' },
    { Header: 'KVKK Onayı', accessor: 'kvkkApproval' },
    { Header: 'Durum', accessor: 'status' },
    { Header: 'Red Nedeni', accessor: 'rejectReason' },
    { Header: 'İşlemler', accessor: 'changes' },
    { Header: 'Güncellenme Tarihi', accessor: 'update' },
    { Header: 'Güncelleyen', accessor: 'acceptingUser' },
  ]

  const tableColumns = useMemo(() => columnDefinitions, [])

  const tableData = useMemo(() => {
    const tableData = cardList?.map((item, index) => {
      const data = {
        order: index + 1,
        bayiKodu: item.DealerCode && item.DealerCode.replace('0000', ''),
        bayiAdı: item.DealerName,
        cardNumber: formatCardNumberGroups(item.CardNumber),
        cardName: item.CardName,
        // Kart görseli artık context servisinden imzalı tam URL olarak geliyor. react-router'ın
        // Link'i mutlak adresi uygulama içi rota sanıp bozduğu için düz <a> kullanılıyor.
        image: item.Image ? (
          <a href={item.Image} target='_blank' rel='noreferrer'>
            <img style={{ cursor: 'pointer' }} src='/images/document-icon.svg' alt='' />
          </a>
        ) : null,
        // Yeni akışta beyan sunucuda üretilip saklanıyor. Eski kayıtlarda hâlâ bayinin
        // yüklediği bir dosya URL'i olabilir, o da desteklenmeye devam ediyor.
        approvalDocument: item.HasDeclaration ? (
          <img
            style={{ cursor: 'pointer' }}
            src='/images/document-icon.svg'
            alt=''
            onClick={() => openDeclaration(item.Id)}
          />
        ) : item.ApprovalDocument ? (
          <a href={item.ApprovalDocument} target='_blank' rel='noreferrer'>
            <img style={{ cursor: 'pointer' }} src='/images/document-icon.svg' alt='' />
          </a>
        ) : null,
        // Sözleşme onayında (DealerContractApproval) yüklenen belge; yüklenmemişse boş.
        imzaSirkusu: item.ImzaSirkusuUrl ? (
          <a href={item.ImzaSirkusuUrl} target='_blank' rel='noreferrer' title='İmza Sirküsü'>
            <img style={{ cursor: 'pointer' }} src='/images/document-icon.svg' alt='' />
          </a>
        ) : null,
        create: item.CreateDate && moment(item.CreateDate).format('DD.MM.YYYY HH:mm:ss'),
        enabled: item.Enabled ? <span style={{ color: 'green' }}>Evet</span> : <span style={{ color: 'red' }}>Hayır</span>,
        deleted: item.Deleted ? <span style={{ color: 'red' }}>Evet</span> : <span style={{ color: 'green' }}>Hayır</span>,
        active: item.Active ? <span style={{ color: 'green' }}>Evet</span> : <span style={{ color: 'red' }}>Hayır</span>,
        kvkkApproval: item.KvkkApproved ? (
          <span
            style={{ color: 'green' }}
            title={item.KvkkApprovalIp ? `IP: ${item.KvkkApprovalIp}` : ''}
          >
            Evet
            {item.KvkkApprovalDate ? ` (${moment(item.KvkkApprovalDate).format('DD.MM.YYYY HH:mm:ss')})` : ''}
          </span>
        ) : (
          <span style={{ color: 'red' }}>Hayır</span>
        ),
        status: (
          <span
            style={{
              color: item.Status === 0 ? 'orange' : item.Status === 1 ? 'red' : item.Status === 2 ? 'green' : '',
            }}
          >
            {filterStatus.find(a => a.value === item.Status)?.label}
          </span>
        ),
        rejectReason: item.Status === 1 ? item.RejectReason : null,
        update: item.AcceptingTime && moment(item.AcceptingTime).format('DD.MM.YYYY HH:mm:ss'),
        acceptingUser:
          item.AcceptingUserDealer &&
          item.AcceptingUserName &&
          '(' + (item.AcceptingUserDealer.replace('0000', '') + ') ' + item.AcceptingUserName),
        changes: (
          <div>
            <a
              data-action-type='edit'
              role='button'
              style={{ cursor: 'pointer' }}
              data-toggle='tooltip'
              data-original-title='Onayla'
              onClick={() => changeStatus(item.Id, 2, item.Active)}
              className='CellWithComment ml-auto'
            >
              <i className='fa fa-thumbs-up' style={{ color: '#29db7f', width: '20px' }}></i>
              <span className='CellComment'>Onayla</span>
            </a>

            <a
              data-action-type='edit'
              role='button'
              style={{ cursor: 'pointer', marginLeft: '10px' }}
              data-toggle='tooltip'
              data-original-title='Red'
              onClick={() => reject(item)}
              className='CellWithComment'
            >
              <i className='fa fa-thumbs-down' style={{ color: 'red', width: '20px' }}></i>
              <span className='CellComment'>Reddet</span>
            </a>

            <a
              data-action-type='edit'
              role='button'
              style={{ cursor: 'pointer', marginLeft: '10px' }}
              data-toggle='tooltip'
              data-original-title='Kapat'
              onClick={() => changeStatus(item.Id, item.Status, !item.Active)}
              className='CellWithComment'
            >
              {item.Active ? (
                <>
                  <i className='fa fa-x' style={{ color: 'red', width: '20px' }}></i>
                  <span className='CellComment'>Kartı Kapat</span>
                </>
              ) : (
                <>
                  <i className='fa fa-check' style={{ color: 'green', width: '20px' }}></i>
                  <span className='CellComment'>Kartı Aç</span>
                </>
              )}
            </a>
          </div>
        ),
      }
      return data
    })

    return { tableData }
  }, [cardList])

  return (
    <div className='card'>
      <div className='content col-lg-12'>
        <div className='card-header px-4'>
          <span className='h4'>Bayi Kredi Kartı Listesi</span>
        </div>

        <div className='card-body'>
          <form className='formbanner'>
            <div className='row'>
              <div className='col-lg-2'>
                <IPInput
                  type='text'
                  className='form-control'
                  id='bayiAdi'
                  placeholder='Bayi Adı'
                  value={state.dealerName}
                  onChange={e => setState({ dealerName: e.target.value })}
                />
              </div>

              <div className='col-lg-2'>
                <IPInput
                  type='text'
                  className='form-control'
                  id='bayiKodu'
                  placeholder='Bayi Kodu'
                  value={state.dealerCode}
                  onChange={e => setState({ dealerCode: e.target.value })}
                />
              </div>

              <div className='col-lg-2'>
                <IPInput
                  type='text'
                  className='form-control'
                  id='cardName'
                  placeholder='Kart Sahibi'
                  value={state.cardName}
                  onChange={e => setState({ cardName: e.target.value })}
                />
              </div>

              <div className='col-lg-2'>
                <IPInput
                  type='text'
                  className='form-control'
                  id='cardNumber'
                  placeholder='Kart Numarası'
                  inputMode='numeric'
                  maxLength={23}
                  controlledField
                  value={formatCardNumberGroups(state.cardNumber)}
                  onChange={e => setState({ cardNumber: compactCardNumber(e.target.value) })}
                />
              </div>

              <div className='col-lg-2'>
                <IPSelectBox
                  isClearable
                  id='Status'
                  name='Status'
                  options={filterStatus}
                  placeholder='Başvuru Durumu'
                  value={state.selectedStatus}
                  onChangeSingle={option => setState({ selectedStatus: option })}
                />
              </div>

              <div className='col-lg-1'>
                <IPCheckbox
                  id='Active'
                  name='Active'
                  label='Aktif'
                  checked={state.isActive}
                  onCheckedChange={() => setState({ isActive: !state.isActive })}
                />
              </div>

              <div className='col-lg-1'>
                <IPButton text='Listele' className='button' onClick={() => getList()} />
              </div>
            </div>
          </form>

          <div style={{ height: 12 }} />

          {/* NOTLAR + KARAR TABLOSU (YARI YARIYA) */}
          <div className='row' style={{ marginTop: 12 }}>
            {/* SOL: NOTLAR */}
            <div className='col-lg-6'>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Notlar</div>

              <span style={{ color: 'red' }}>
                Sadece kendi kartıyla ödeme yapmasını istediğiniz bayiler için <b>KENDIKARTIDAHIL</b> adındaki alt tipe tanım yapınız.
              </span>
              <br />

              <span style={{ color: 'red' }}>
                Bütün bayileri kart kontrolüne dahil etmek için <b>KENDIKARTIDAHILHERKES</b> alt tipine ait herhangi bir bayi eklemeniz gerekmektedir.
              </span>
              <br />

              <span style={{ color: 'red' }}>
                Sadece kendi kartıyla ödeme yapmasını istemediğiniz bayiler için <b>KENDIKARTIHARIC</b> adındaki alt tipe tanım yapınız.
              </span>
              <br />

              <span style={{ color: 'red' }}>
                Hiçbir alt tipe tanım yapılmamış bayiler için kendi kartıyla ödeme kontrolü çalışmamaktadır.
              </span>
              <br />

              <span style={{ color: 'red' }}>Alt tip kontrolleri <b>şirket özelinde</b> çalışmaktadır.</span>
            </div>

            {/* SAĞ: TABLO */}
            <div className='col-lg-6'>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>
                Kart Kontrolü Karar Tablosu
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className='table table-bordered table-sm' style={{ width: '100%', fontSize: 14 }}>
                  <thead>
                    <tr>
                      <th>KENDIKARTIDAHILHERKES</th>
                      <th>KENDIKARTIDAHIL</th>
                      <th>KENDIKARTIHARIC</th>
                      <th className='text-nowrap' style={{ width: 170 }}>Kart Kontrolü</th>                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Hayır</td>
                      <td>Hayır</td>
                      <td>Hayır</td>
                      <td>Hayır</td>
                    </tr>
                    <tr>
                      <td>Evet</td>
                      <td>Hayır</td>
                      <td>Hayır</td>
                      <td style={{ fontWeight: 700 }}>Evet</td>
                    </tr>
                    <tr>
                      <td>Hayır</td>
                      <td>Evet</td>
                      <td>Hayır</td>
                      <td style={{ fontWeight: 700 }}>Evet</td>
                    </tr>
                    <tr>
                      <td>Evet</td>
                      <td>Evet</td>
                      <td>Hayır</td>
                      <td style={{ fontWeight: 700 }}>Evet</td>
                    </tr>
                    <tr>
                      <td>Hayır</td>
                      <td>Hayır</td>
                      <td>Evet</td>
                      <td>Hayır (HARİÇ baskın)</td>
                    </tr>
                    <tr>
                      <td>Evet</td>
                      <td>Hayır</td>
                      <td>Evet</td>
                      <td>Hayır (HARİÇ baskın)</td>
                    </tr>
                    <tr>
                      <td>Hayır</td>
                      <td>Evet</td>
                      <td>Evet</td>
                      <td>Hayır (HARİÇ baskın)</td>
                    </tr>
                    <tr>
                      <td>Evet</td>
                      <td>Evet</td>
                      <td>Evet</td>
                      <td>Hayır (HARİÇ baskın)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ height: 16 }} />

          <div>
            <Table columns={tableColumns} data={tableData.tableData} currentPage={0} columnCount={19} />
          </div>
        </div>
      </div>
    </div>
  )
}

function GuardedIndex(props: BaseComponentWithContextProps) {
  return (
    <AdminPageAuthGuard route='DealerCreditCardAuth' title='Bayi Kredi Kartı Listesi'>
      <Index {...props} />
    </AdminPageAuthGuard>
  )
}

export default componentWithContext(GuardedIndex)